/**
 * Base Action class for all button behaviors
 * Provides a reusable, OOP-based action system for accessibility menu components
 */

import { apiFetch, apiPost } from './apiConfig';
import * as VolumeService from '../services/VolumeService';

export const ActionTypes = {
  NoAction: 'none',
  Navigate: 'navigate',
  ToggleMode: 'toggleMode',
  VehicleFunction: 'vehicleFunction',
  ChangeMode: 'changeMode',
  Endpoint: 'endpoint',
  ScriptedCallback: 'scriptedCallback'
}

class BaseAction {
  constructor(type, options = {}) {
    this.type = type;
    this.options = options;
    this.timestamp = null;
  }

  /**
   * Execute the action
   * @param {Object} context - Action context (component, user, etc.)
   * @returns {Promise<ActionResult>}
   */
  async execute(context) {
    this.timestamp = Date.now();
    // Base implementation - subclasses can override
    return {
      success: true,
      action: this.type,
      timestamp: this.timestamp
    };
  }

  /**
   * Get compressed data for GM/OnStar transmission
   * @returns {Object} Ultra-compact action data
   */
  getCompressedData(context) {
    return {
      t: Math.floor(this.timestamp / 1000), // 4-byte timestamp
      a: this.getActionCode(), // 1-byte action type
      ...this.getAdditionalCompressedData(context)
    };
  }

  getActionCode() {
    throw new Error('getActionCode() must be implemented by action subclass');
  }

  getAdditionalCompressedData(context) {
    return {};
  }

  /**
   * Serialize action for storage/transmission
   */
  serialize() {
    return {
      type: this.type,
      options: this.options
    };
  }

  /**
   * Create action from serialized data
   */
  static deserialize(data) {
    return ActionFactory.createAction(data.type, data.options);
  }
}

/**
 * Navigation Action - Navigate between UI pages
 */
class NavigateAction extends BaseAction {
  constructor(targetPage, options = {}) {
    super(ActionTypes.Navigate, { targetPage, ...options });
    this.targetPage = targetPage;
  }

  async execute(context) {
    await super.execute(context);
    
    console.log(`🧭 Navigate Action: ${context.currentPage} → ${this.targetPage}`);
    
    // Execute navigation
    if (context.navigateToPage) {
      context.navigateToPage(this.targetPage);
    } else {
      console.warn('⚠️ Navigation handler not available in context');
    }

    return {
      success: true,
      action: ActionTypes.Navigate,
      from: context.currentPage,
      to: this.targetPage,
      timestamp: this.timestamp
    };
  }

  getActionCode() {
    return 1; // Navigate = 1
  }

  getAdditionalCompressedData(context) {
    // Page mapping for compression (could be expanded)
    const pageMap = { 'Main': 0, 'Settings': 1 };
    return {
      pg: pageMap[this.targetPage] || 255 // 1-byte page ID
    };
  }
}

/**
 * Toggle Mode Action - Switch between edit and preview modes
 */
class ToggleModeAction extends BaseAction {
  constructor(options = {}) {
    super(ActionTypes.ToggleMode, options);
  }

  async execute(context) {
    await super.execute(context);
    console.log(`👁️ Toggle Mode Action triggered`);

    // Detect current mode from URL
    const urlParams = new URLSearchParams(window.location.search);
    const currentMode = urlParams.get('mode');

    // If we're currently in vehicle mode, toggle back to editor
    if (currentMode === 'vehicle') {
      console.log('👁️ Toggle Mode Action: Vehicle → Edit');

      // Build editor URL by setting mode to editor and preserving config when available
      urlParams.set('mode', 'editor');
      if (context.currentConfigId) {
        urlParams.set('config', context.currentConfigId);
      }
      const editorQuery = urlParams.toString();
      const editorUrl = `${window.location.origin}${window.location.pathname}?${editorQuery}`;
      window.location.href = editorUrl;

      return {
        success: true,
        action: ActionTypes.ToggleMode,
        to: 'editor',
        configId: context.currentConfigId,
        timestamp: this.timestamp
      };
    }

    // Otherwise, we're in edit mode and want to switch to vehicle preview
    console.log('👁️ Toggle Mode Action: Edit → Vehicle Preview');

    // Validate configuration exists
    if (!context.currentConfigId) {
      const error = '❌ Please save the configuration first before switching to preview mode';
      alert(error);
      return {
        success: false,
        error,
        timestamp: this.timestamp
      };
    }

    // Switch to vehicle mode
    const vehicleUrl = `${window.location.origin}${window.location.pathname}?mode=vehicle&config=${context.currentConfigId}`;
    window.location.href = vehicleUrl;

    return {
      success: true,
      action: ActionTypes.ToggleMode,
      to: 'vehicle',
      configId: context.currentConfigId,
      timestamp: this.timestamp
    };
  }

  getActionCode() {
    return 2; // ToggleMode = 2
  }

  getAdditionalCompressedData(context) {
    return {
      cfg: context.currentConfigId ? 1 : 0 // 1-bit config status
    };
  }
}

/**
 * Vehicle Function Action - Control actual vehicle systems
 */
class VehicleFunctionAction extends BaseAction {
  constructor(category, functionName, options = {}) {
    super(ActionTypes.VehicleFunction, { category, functionName, ...options });
    this.category = category;
    this.functionName = functionName;
    this.endpoint = options.endpoint;
    this.behaviorType = options.behaviorType || 'momentary';
  }

  async execute(context) {
    console.log('context in VehicleFunctionAction.execute:', context);
    await super.execute(context);
    
    console.log(`🚗 Vehicle Function: ${this.category}.${this.functionName}`);
    console.log(`📊 Category: "${this.category}", Function: "${this.functionName}", Type: "${this.behaviorType}"`);
    
    // Generate optimized 6-byte packet for OnStar transmission
    const packet = await this.getCompressedData(context);

    if (packet.fn >= 0) {
    
      try {
        // Send both 6-byte packet and readable data to server
        const requestData = {
          packet: packet,          // Optimized 6-byte format for OnStar
          readable: {              // Rich data for development/logging
            action: ActionTypes.VehicleFunction,
            componentId: context.componentId,
            userId: context.userId,
            profileType: context.profileType,
            vehicleFunction: {
              category: this.category,
              functionName: this.functionName,
              behaviorType: this.behaviorType,
              endpoint: this.endpoint
            }
          }
        };
        
        console.log('📡 Sending 6-byte packet to vehicle system:', packet);
        
        // Send to backend server
        const response = await apiPost('/api/vehicle/action', requestData);
        
        if (response.ok) {
          const result = await response.json();
          console.log('✅ Server response:', result);
        } else {
          console.error('❌ Server error:', response.status, response.statusText);
        }
        
        return {
          success: true,
          action: ActionTypes.VehicleFunction,
          category: this.category,
          function: this.functionName,
          packet,
          timestamp: this.timestamp
        };
      } catch (error) {
        console.error('❌ Vehicle communication failed:', error);
        return {
          success: false,
          error: error.message,
          timestamp: this.timestamp
        };
      }
    }
  }

  getActionCode() {
    return 3; // VehicleFunction = 3
  }

  async getCompressedData(context) {
    // Get the safe, collision-free function code (16-bit)
    const functionCode = await this.getFunctionCode();
    
    // Pack control data into 32-bit value
    const controlData = await this.packControlData(context);
    
    // Generate 6-byte packet: 2-byte function code + 4-byte control data
    const packet = {
      fn: functionCode,        // 2-byte function code
      ctrl: controlData        // 4-byte packed control data
    };

    if (functionCode > 0) {
      console.log(`📦 6-byte packet generated: fn=${functionCode} (0x${functionCode.toString(16).padStart(4, '0')}), ctrl=${controlData} (0x${controlData.toString(16).padStart(8, '0')})`);
    }

    return packet;
  }

  async packControlData(context) {
    // Behavior type mapping (3 bits - supports 8 types)
    const behaviorMap = {
      'momentary': 0, 'toggle': 1, 'long_press': 2, 'slider': 3
    };

    // Pack 32-bit control data:
    // Bits 31-29: Behavior type (3 bits)
    // Bit 28:     Toggle state (1 bit) 
    // Bits 27-16: Reserved for future scheduling (12 bits)
    // Bits 15-0:  Reserved for value/parameter data (16 bits)
    
    let controlData = 0;
    
    // Pack behavior type (bits 31-29)
    const behaviorType = behaviorMap[this.behaviorType] || 0;
    controlData |= (behaviorType & 0x7) << 29;
    
    // Pack toggle state (bit 28)
    if (context.toggleState !== undefined && context.toggleValue !== undefined) {
      const toggleValue = context.toggleValue ? 1 : 0;
      controlData |= (toggleValue & 0x1) << 28;
      console.log(`🔄 Toggle state packed: ${context.toggleState} (${context.toggleValue ? 'ON' : 'OFF'})`);
    }
    
    // Future: Pack scheduling data in bits 27-16
    // Future: Pack parameter/value data in bits 15-0
    
    return controlData >>> 0; // Ensure unsigned 32-bit
  }

  // Clear cache when needed (for testing or updates)
  static clearCategoryCache() {
    delete VehicleFunctionAction._categoryCache;
  }

  async getFunctionCode() {
    // Use the manually assigned function codes from vehicle-hmi-features.json
    // This replaces the dangerous hash-based approach that caused collisions
    try {
      const response = await apiFetch('/api/vehicle-hmi-features');
      if (!response.ok) {
        throw new Error(`Failed to load HMI features: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(this.category);
      const categoryData = data.vehicleHMIFeatures[this.category];
      console.log(categoryData);
      
      if (!categoryData || !categoryData.features) {
        return -1; // Error code for unknown category
      }
      
      const featureData = categoryData.features[this.functionName];
      if (!featureData || !featureData.functionCode) {
        console.error(`❌ Function "${this.functionName}" not found in category "${this.category}"`);
        return 65534; // Error code for unknown function
      }
      
      console.log(`✅ Function code for ${this.category}.${this.functionName}: ${featureData.functionCode}`);
      return featureData.functionCode;
    } catch (error) {
      console.error(`❌ Error loading function code for ${this.category}.${this.functionName}:`, error);
      return 65533; // Error code for lookup failure
    }
  }
}

class ScriptedCallbackAction extends BaseAction {
  constructor(category, functionName, options = {}) {
    super(ActionTypes.VehicleFunction, { category, functionName, ...options });
    this.category = category;
    this.functionName = functionName;
    this.endpoint = options.endpoint;
    this.behaviorType = options.behaviorType || 'momentary';
    this.result = null;
    this.callbackMap = {
      volumeDown: VolumeService.volumeDown,
      volumeUp: VolumeService.volumeUp,
      getUiVolume: VolumeService.getUiVolume
    };

  }

  async execute(context) {
    console.log('context in ScriptedCallbackAction.execute:', context);
    await super.execute(context);

    console.log(`🖥️ Scripted Callback: ${this.category}.${this.functionName}`);
    console.log('options:', this.options);

    const toCall = this.options.callbackName;
    if (toCall && typeof this.callbackMap[toCall] === 'function') {
      console.log(`🔔 Invoking scripted callback function: ${toCall}`);
      const callbackResult = this.callbackMap[toCall](context);
      this.result = callbackResult instanceof Promise ? await callbackResult : callbackResult;
      console.log('🔔 Callback result:', this.result);
    }

    return {
      success: true,
      action: ActionTypes.ScriptedCallback,
      result: this.result
    }
  }
}

/**
 * Action Factory - Creates action objects from configuration
 */
class ActionFactory {
  static createAction(type, options = {}) {
    switch (type) {
      case ActionTypes.Navigate:
        return new NavigateAction(options.targetPage, options);
      
      case ActionTypes.ToggleMode:
        return new ToggleModeAction(options);
      
      case ActionTypes.VehicleFunction:
        return new VehicleFunctionAction(
          options.category, 
          options.functionName, 
          options
        );

      case ActionTypes.Endpoint:
        return new VehicleFunctionAction(
          options.category, 
          options.functionName, 
          options
        );
      
      case ActionTypes.ScriptedCallback:
        return new ScriptedCallbackAction(
          options.category,
          options.functionName,
          options
        );
      
      default:
        throw new Error(`Unknown action type: ${type}`);
    }
  }

  /**
   * Create action from component behavior configuration
   */
  static fromBehavior(behavior) {
    if (!behavior || !behavior.action) {
      throw new Error('Invalid behavior configuration');
    }

    switch (behavior.action) {
      case ActionTypes.Navigate:
        return new NavigateAction(behavior.targetPage, behavior);
      
      case ActionTypes.ToggleMode:
        return new ToggleModeAction(behavior);
      
      case ActionTypes.VehicleFunction:
        return new VehicleFunctionAction(
          behavior.category || 'unknownVehicleFunctionCategory',
          behavior.functionName || behavior.action,
          behavior
        );

      case ActionTypes.Endpoint:
        return new VehicleFunctionAction(
          behavior.category || 'unknownEndpointCategory',
          behavior.functionName || behavior.action,
          behavior
        );

      case ActionTypes.ScriptedCallback:
        return new ScriptedCallbackAction(
          behavior.category || 'unknownScriptedCallbackCategory',
          behavior.functionName || behavior.action,
          behavior
        );
      
      default:
        // For unknown actions, throw an error instead of defaulting
        throw new Error(`Unsupported action type: ${behavior.action}`);
    }
  }
}

/**
 * Action Dispatcher - Centralized action execution
 */
class ActionDispatcher {
  constructor() {
    this.actionHistory = [];
    this.listeners = [];
  }

  /**
   * Execute an action with full context
   */
  async executeAction(action, context = {}) {
    try {
      const result = await action.execute(context);
      
      // Log to history
      this.actionHistory.push({
        action: action.serialize(),
        result,
        context: {
          componentId: context.componentId,
          userId: context.userId,
          profileType: context.profileType
        }
      });

      // Notify listeners
      this.notifyListeners(action, result, context);

      return result;
    } catch (error) {
      console.error('❌ Action execution failed:', error);
      return {
        success: false,
        error: error.message,
        timestamp: Date.now()
      };
    }
  }

  /**
   * Add action result listener
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  notifyListeners(action, result, context) {
    this.listeners.forEach(callback => {
      try {
        callback(action, result, context);
      } catch (error) {
        console.error('Action listener error:', error);
      }
    });
  }

  /**
   * Get compressed action history for transmission
   */
  getCompressedHistory(limit = 100) {
    return this.actionHistory
      .slice(-limit)
      .map(entry => entry.action);
  }
}

// Export for use in components
export {
  BaseAction,
  NavigateAction,
  ToggleModeAction,
  VehicleFunctionAction,
  ScriptedCallbackAction,
  ActionFactory,
  ActionDispatcher
};

// Global action dispatcher instance
export const actionDispatcher = new ActionDispatcher();